<script setup lang="ts">
  import type { StashColorNamesWithShades } from '@leaflink/stash-types/colors';
  import { computed } from 'vue';

  import type { IconName } from '../Icon/Icon.types';
  import Icon from '../Icon/Icon.vue';

  export interface IconLabelProps {
    /**
     * Name of the icon to use. All design system icons are supported.
     */
    icon: IconName;

    /**
     * Color to apply to the icon. Common Stash colors are supported (100, 500, & 700 shades).
     */
    color?: StashColorNamesWithShades | 'white' | 'black';

    /**
     * Valid hyperlink.
     */
    href?: string;

    /**
     * Valid vue-router route.
     */
    to?: string | object;

    /**
     * If true, renders the label below the icon instead of to the right
     */
    stacked?: boolean;

    /**
     * If true, truncates overflowing text with an ellipsis when it's unable to wrap
     */
    truncate?: boolean;
  }

  const props = withDefaults(defineProps<IconLabelProps>(), {
    color: undefined,
    href: undefined,
    to: undefined,
    stacked: false,
    truncate: false,
  });

  defineOptions({
    name: 'll-icon-label',
  });

  const is = computed(() => {
    if (props.to) {
      return 'router-link';
    }

    if (props.href) {
      return 'a';
    }

    return 'div';
  });

  const attributes = computed(() => {
    if (props.to) {
      return {
        to: props.to,
      };
    }

    if (props.href) {
      return {
        href: props.href,
      };
    }

    return {};
  });
</script>

<template>
  <component :is="is" v-bind="attributes" class="flex" :class="{ 'flex-col items-center': props.stacked }">
    <Icon :name="props.icon" size="dense" :class="[$style.icon, { [`text-${color}`]: !!color }]" />

    <div
      :class="{
        'ml-1.5 flex-1': !props.stacked,
        'mt-1.5 text-xs leading-3': props.stacked,
        truncate: truncate,
      }"
    >
      <!-- @slot Label text -->
      <slot></slot>
    </div>
  </component>
</template>

<style module>
  .icon {
    margin-top: -1px;
  }
</style>
